import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Phone, Mail, AlertTriangle, Heart } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../App';

const EmergencyView = () => {
  const { profileId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/emergency-view/${profileId}`);
      setProfile(response.data);
      setLoading(false);
    } catch (error) {
      setError('Unable to load emergency profile');
      setLoading(false);
    }
  };

  const callEmergency = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const emailContact = (email) => {
    window.location.href = `mailto:${email}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Not Found</h2>
          <p className="text-gray-600">This emergency health profile could not be found or has been removed.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-12 h-12 text-red-600" />
            <h1 className="text-4xl font-bold text-gray-800">Emergency Health Profile</h1>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">For Emergency Use Only</span>
          </div>
        </div>

        {/* Patient Info */}
        <Card className="p-6 mb-6 bg-white shadow-xl border-2 border-red-200">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-red-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{profile.user_name}</h2>
              {profile.last_updated && (
                <p className="text-sm text-gray-500">
                  Last Updated: {new Date(profile.last_updated).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Blood Type */}
          {profile.blood_type && (
            <Card className="p-6 bg-gradient-to-br from-red-50 to-pink-50 border-2 border-red-200">
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Blood Type</h3>
              <p className="text-3xl font-bold text-red-600">{profile.blood_type}</p>
            </Card>
          )}

          {/* Allergies */}
          <Card className="p-6 bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200">
            <h3 className="font-bold text-gray-800 mb-3 text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Allergies
            </h3>
            {profile.allergies && profile.allergies.length > 0 ? (
              <ul className="space-y-2">
                {profile.allergies.map((allergy, index) => (
                  <li key={`item-${index}`} className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-orange-600 rounded-full mt-2"></span>
                    <span className="text-gray-700">{allergy}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">No known allergies</p>
            )}
          </Card>

          {/* Chronic Conditions */}
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
            <h3 className="font-bold text-gray-800 mb-3 text-lg">Chronic Conditions</h3>
            {profile.chronic_conditions && profile.chronic_conditions.length > 0 ? (
              <ul className="space-y-2">
                {profile.chronic_conditions.map((condition, index) => (
                  <li key={`item-${index}`} className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                    <span className="text-gray-700">{condition}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">None reported</p>
            )}
          </Card>

          {/* Current Medications */}
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
            <h3 className="font-bold text-gray-800 mb-3 text-lg">Current Medications</h3>
            {profile.current_medications && profile.current_medications.length > 0 ? (
              <ul className="space-y-2">
                {profile.current_medications.map((medication, index) => (
                  <li key={`item-${index}`} className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-purple-600 rounded-full mt-2"></span>
                    <span className="text-gray-700">{medication}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">None reported</p>
            )}
          </Card>
        </div>

        {/* Additional Notes */}
        {profile.additional_notes && (
          <Card className="p-6 mt-6 bg-gray-50 border-2 border-gray-200">
            <h3 className="font-bold text-gray-800 mb-2 text-lg">Additional Notes</h3>
            <p className="text-gray-700 whitespace-pre-line">{profile.additional_notes}</p>
          </Card>
        )}

        {/* Emergency Contacts */}
        {profile.emergency_contacts && profile.emergency_contacts.length > 0 && (
          <Card className="p-6 mt-6 bg-white shadow-xl border-2 border-green-200">
            <h3 className="font-bold text-gray-800 mb-4 text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              Emergency Contacts
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {profile.emergency_contacts.map((contact, index) => (
                <Card key={`item-${index}`} className="p-4 bg-green-50">
                  <div className="mb-3">
                    <p className="font-bold text-gray-800">{contact.name}</p>
                    <p className="text-sm text-gray-600">{contact.relationship}</p>
                  </div>
                  <div className="space-y-2">
                    <Button
                      onClick={() => callEmergency(contact.phone)}
                      className="w-full bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {contact.phone}
                    </Button>
                    {contact.email && (
                      <Button
                        onClick={() => emailContact(contact.email)}
                        variant="outline"
                        className="w-full"
                        size="sm"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {contact.email}
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            This information is provided for emergency medical purposes only.
            <br />
            Treat all information as confidential medical data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmergencyView;
